import React from "react";
import { Card, CardHeader, CardTitle, Splitter, SplitterPane, SplitterOnChangeEvent, TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import { Loader } from "@progress/kendo-react-indicators";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useShallow } from "zustand/react/shallow";
import { useFileExplorerStore } from "@presentation/stores/FileExplorerStore";
import { useFastaExplorerStore } from "@presentation/stores/FastaExplorerStore";
import { getVisibleRightPanelTabKeys } from "@presentation/components/job/components/rightPanelTabs";
import { useInjection } from "@presentation/components/dependencyInjection";
import { LibraryRepository } from "@infrastructure/repositories/LibraryRepository";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { FastaFile, FileMetadata, JobPipelineTypeSchema, SettingsTypeEnumSchema, SoftwareSettingsMetadata, StatusEnumSchema } from "@domain/schemas/BI";
import { useLocation } from "react-router-dom";
import { ListJobsService } from "@application/useCases/job/ListJobTasksService";
import { ListFastaService, ListSoftwareSettingsService } from "@application/useCases";
import { pageFillContentStyle, pageHeaderRegionStyle, pageHeaderStyle, pageShellStyle, pageTitleStyle } from "@presentation/common/pageLayout";

import JobSetupForm from "@presentation/components/job/JobSetupForm";
import "@presentation/pages/styles/JobSetupPage.scss";
import { FileExplorerGrid } from "./FileExplorerPage";
import { LibraryExplorerGrid } from "@presentation/components/inventory/LibraryExplorerGrid";
import { ArchiveExplorerGrid, ArchiveGridItem } from "@presentation/components/inventory/ArchiveExplorerGrid";
import { ArchiveRepository } from "@infrastructure/repositories/ArchiveRepository";
import { RawFileRepository } from "@infrastructure/repositories";
import type { IStorageLocationsRepository } from "@domain/repositories/IStorageLocationsRepository";
import { TYPES } from "@utils/inversifyJS/types";
import { FastaExplorerGrid, buildRowKey } from "@presentation/components/fasta/FastaExplorerGrid";
import { SoftwareSettingsExplorerGrid } from "@presentation/components/job/components/SoftwareSettingsExplorerGrid";
import { getVisibleSettingsSections } from "@domain/software";
import { useWorkspaceFilters } from "@presentation/hooks/useWorkspaceFilters";

const CreateJobFormCard: React.FC<{
  isAdvancedSettingsExpanded: boolean;
  onAdvancedSettingsExpandedChange: (isExpanded: boolean) => void;
}> = ({ isAdvancedSettingsExpanded, onAdvancedSettingsExpandedChange }) => {
  const location = useLocation();
  const activeProjectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const injectedListJobsService = useInjection(ListJobsService);
  const injectedListFastaService = useInjection(ListFastaService);
  const injectedListSoftwareSettingsService = useInjection(ListSoftwareSettingsService);
  const injectedRawFileRepository = useInjection(RawFileRepository);
  const injectedStorageLocationsRepository = useInjection<IStorageLocationsRepository>(TYPES.IStorageLocationsRepository);
  const listJobsServiceReference = React.useRef(injectedListJobsService);
  const listFastaServiceReference = React.useRef(injectedListFastaService);
  const listSoftwareSettingsServiceReference = React.useRef(injectedListSoftwareSettingsService);
  const rawFileRepositoryReference = React.useRef(injectedRawFileRepository);
  const storageLocationsRepositoryReference = React.useRef(injectedStorageLocationsRepository);
  const { loadExistingJobForRerun, resetCreateJobState } = useJobSetupStore(
    useShallow((state) => ({
      loadExistingJobForRerun: state.loadExistingJobForRerun,
      setRerunAvailableSteps: state.setRerunAvailableSteps,
      resetCreateJobState: state.resetCreateJobState,
    })),
  );

  React.useEffect(() => {
    let isCancelled = false;

    const loadMergeNodePools = async () => {
      const store = useJobSetupStore.getState();
      store.setMergeNodePoolOptionsLoading(true);
      store.setMergeNodePoolOptionsError(null);

      try {
        const options = await listJobsServiceReference.current.getAvailableMergeNodePoolsAsync();
        if (!isCancelled) {
          store.setMergeNodePoolOptions(options);
        }
      } catch (error) {
        console.error("Failed to load merge node pool options", error);
        if (!isCancelled) {
          store.setMergeNodePoolOptions([]);
          store.setMergeNodePoolOptionsError("Could not load merge node pool options. Default merge pool will be used.");
        }
      } finally {
        if (!isCancelled) {
          store.setMergeNodePoolOptionsLoading(false);
        }
      }
    };

    loadMergeNodePools();

    return () => {
      isCancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const normalizePath = (path?: string | null) => (path ?? "").replaceAll("\\", "/").replaceAll(/^\/+|\/+$/g, "");
    const toFullPath = (location?: string | null, name?: string | null) => normalizePath([location, name].filter(Boolean).join("/"));
    const toLocationAndName = (path: string) => {
      const normalized = normalizePath(path);
      const parts = normalized.split("/").filter(Boolean);
      return {
        location: parts.slice(0, -1).join("/"),
        name: parts.at(-1) ?? normalized,
      };
    };

    const toFallbackFasta = (path: string, createdBy: string): FastaFile => {
      const { location, name } = toLocationAndName(path);
      return {
        createdBy,
        location,
        fileName: name,
        size: 0,
      };
    };

    const toFallbackSetting = (
      path: string,
      createdBy: string,
      softwareVersion: string,
      settingsType: SettingsTypeEnumSchema,
      software: SoftwareSettingsMetadata["software"],
    ): SoftwareSettingsMetadata => {
      const { location, name } = toLocationAndName(path);
      return {
        id: normalizePath(path),
        name,
        location,
        createdBy,
        software,
        softwareVersion,
        settingsType,
      } as SoftwareSettingsMetadata;
    };

    const stripStorageBasePath = (path: string, basePath: string) => {
      const normalizedPath = normalizePath(path);
      const normalizedBase = normalizePath(basePath);
      if (!normalizedBase) return normalizedPath;
      return normalizedPath.startsWith(`${normalizedBase}/`) ? normalizedPath.slice(normalizedBase.length + 1) : normalizedPath;
    };

    const searchParameters = new URLSearchParams(location.search);
    const rerunJobId = searchParameters.get("rerunJobId");

    if (!rerunJobId) {
      resetCreateJobState();
      useFastaExplorerStore.getState().setSelectedState({});
      useFileExplorerStore.getState().setSelectedState({});
      return;
    }

    // Waits for the active project to resolve - the fasta/software-settings lists below are project-scoped.
    if (!activeProjectId) return;

    let isCancelled = false;

    /**
     * Rebuilds the Data Explorer selection from the source job's raw files.
     *
     * Two translations are required, otherwise the explorer looks empty even though the duplicated
     * job does carry the raw files:
     *  - Jobs persist raw file paths prefixed with the raw-file storage `basePath`
     *    (`CreateJobUseCase.NormalizeRawStoragePath`), while the explorer keys rows by the
     *    base-path-relative id returned by `ListRawFiles`. Un-stripped keys never match a row,
     *    so no checkbox is ticked and folder roll-ups stay empty.
     *  - The job DTO carries no file sizes, so they are re-resolved from storage to keep the
     *    selection statistics and the raw file summary accurate.
     */
    const repopulateRawFileSelection = async (sourceRawFiles: string[] | null | undefined) => {
      let rawFilesBasePath = "";
      try {
        const storageLocations = await storageLocationsRepositoryReference.current.getStorageLocationsAsync();
        rawFilesBasePath = storageLocations.rawFiles?.basePath ?? "";
      } catch (error) {
        console.error("Could not resolve the raw file storage location while duplicating the job", error);
      }
      if (isCancelled) return;

      const explorerPaths = [...new Set((sourceRawFiles ?? []).map((path) => stripStorageBasePath(path, rawFilesBasePath)).filter(Boolean))];

      let sizeByPath: Record<string, number | null> = {};
      if (activeProjectId && explorerPaths.length > 0) {
        try {
          sizeByPath = await rawFileRepositoryReference.current.checkFilesExist(activeProjectId, explorerPaths);
        } catch (error) {
          console.error("Could not resolve raw file sizes while duplicating the job", error);
        }
      }
      if (isCancelled) return;

      useFileExplorerStore.getState().setSelectedState(Object.fromEntries(explorerPaths.map((path) => [path, { path, sizeInBytes: sizeByPath[path] ?? 0, isFolder: false }])));
      useJobSetupStore.getState().setJobRawFiles(explorerPaths.map((path) => ({ path, sizeInBytes: sizeByPath[path] ?? 0 })));
    };

    const loadJob = async () => {
      try {
        const job = await listJobsServiceReference.current.getJobByIDAsync(rerunJobId);

        if (!isCancelled) {
          loadExistingJobForRerun(job);
          await repopulateRawFileSelection(job.rawFiles);
          if (isCancelled) return;
          useFastaExplorerStore
            .getState()
            .setSelectedState(Object.fromEntries((job.fastaFiles ?? []).map((path) => [normalizePath(path), { path: normalizePath(path), sizeInBytes: 0, isFolder: false }])));
          const stepDetails = await listJobsServiceReference.current.getJobStepListAsync(rerunJobId);
          if (isCancelled) return;
          useJobSetupStore.getState().setRerunAvailableSteps(stepDetails, job.status !== StatusEnumSchema.Completed);

          const sourceParallelStep = stepDetails.filter((step) => step.isParallel && (step.batchesCount ?? 0) > 1).toSorted((a, b) => a.position - b.position)[0];

          if (sourceParallelStep) {
            const batchResponse = await listJobsServiceReference.current.getJobStepBatchListAsync(rerunJobId, sourceParallelStep.id);
            if (isCancelled) return;

            const inferredBatchSize = batchResponse.list?.[0]?.rawFileIds?.length;
            if (typeof inferredBatchSize === "number" && inferredBatchSize > 0) {
              useJobSetupStore.getState().setBatchSize(inferredBatchSize);
            }
          }

          const [fastaResponse, experimentResponse, conditionResponse, modificationResponse, reportResponse, goResponse, searchSettingsResponse] = await Promise.all([
            listFastaServiceReference.current.listFastaAsync(activeProjectId),
            listSoftwareSettingsServiceReference.current.getSettingsListAsync(activeProjectId, SettingsTypeEnumSchema.Experiment),
            listSoftwareSettingsServiceReference.current.getSettingsListAsync(activeProjectId, SettingsTypeEnumSchema.ConditionSetup),
            listSoftwareSettingsServiceReference.current.getSettingsListAsync(activeProjectId, SettingsTypeEnumSchema.Modification),
            listSoftwareSettingsServiceReference.current.getSettingsListAsync(activeProjectId, SettingsTypeEnumSchema.Report),
            listSoftwareSettingsServiceReference.current.getSettingsListAsync(activeProjectId, SettingsTypeEnumSchema.GO),
            listSoftwareSettingsServiceReference.current.getSettingsListAsync(activeProjectId, SettingsTypeEnumSchema.SpectroMineSearch),
          ]);

          if (isCancelled) return;

          const fastaList = fastaResponse.list ?? [];
          const fastaByPath = new Map(fastaList.map((item) => [toFullPath(item.location, item.fileName), item]));

          const selectedFasta = (job.fastaFiles ?? []).map((path) => {
            const normalizedPath = normalizePath(path);
            return fastaByPath.get(normalizedPath) ?? toFallbackFasta(path, job.createdBy ?? "");
          });
          useFastaExplorerStore.getState().setSelectedState(
            Object.fromEntries(
              selectedFasta.map((fasta) => {
                const path = toFullPath(fasta.location, fasta.fileName);
                return [buildRowKey(path, fasta.sourcePath ?? null), { path, sizeInBytes: fasta.size ?? 0, isFolder: false, sourcePath: fasta.sourcePath ?? null }];
              }),
            ),
          );

          const jobSetupStore = useJobSetupStore.getState();

          const preserveSingleSelection = (sourcePath: string | null | undefined, currentSelection: SoftwareSettingsMetadata | null, resolvedSelection: SoftwareSettingsMetadata[]) => {
            if (!sourcePath) return [];
            if (!currentSelection) return resolvedSelection;

            const normalizedSourcePath = normalizePath(sourcePath);
            const currentSelectionPath = toFullPath(currentSelection.location, currentSelection.name);

            // Only upgrade the original duplicated selection from fallback metadata.
            // If the user already picked a different file while async data was loading, keep it.
            return currentSelectionPath === normalizedSourcePath ? resolvedSelection : [currentSelection];
          };

          const preserveManySelection = (sourcePaths: string[] | null | undefined, currentSelection: SoftwareSettingsMetadata[] | null, resolvedSelection: SoftwareSettingsMetadata[]) => {
            if (!sourcePaths?.length) return [];
            if (!currentSelection?.length) return resolvedSelection;

            const normalizedSourcePaths = sourcePaths.map((path) => normalizePath(path)).toSorted((a, b) => a.localeCompare(b));
            const currentSelectionPaths = currentSelection.map((setting) => toFullPath(setting.location, setting.name)).toSorted((a, b) => a.localeCompare(b));

            // Only replace the selection when it still matches the duplicated source values.
            // Otherwise preserve any user changes made after the duplicate form opened.
            return JSON.stringify(currentSelectionPaths) === JSON.stringify(normalizedSourcePaths) ? resolvedSelection : currentSelection;
          };

          const resolveSingleSetting = (path: string | null | undefined, list: SoftwareSettingsMetadata[], settingsType: SettingsTypeEnumSchema) => {
            if (!path) return [];

            const normalizedPath = normalizePath(path);
            const match = list.find((item) => toFullPath(item.location, item.name) === normalizedPath);
            return [match ?? toFallbackSetting(path, job.createdBy ?? "", job.softwareVersion ?? "", settingsType, job.software)];
          };

          const resolveManySettings = (paths: string[] | null | undefined, list: SoftwareSettingsMetadata[], settingsType: SettingsTypeEnumSchema) =>
            (paths ?? []).map((path) => {
              const normalizedPath = normalizePath(path);
              return (
                list.find((item) => toFullPath(item.location, item.name) === normalizedPath) ?? toFallbackSetting(path, job.createdBy ?? "", job.softwareVersion ?? "", settingsType, job.software)
              );
            });

          jobSetupStore.setDirectDIASettings(
            preserveSingleSelection(
              job.softwareSettingsFile,
              jobSetupStore.settingsDirectDIA,
              resolveSingleSetting(job.softwareSettingsFile, experimentResponse.list ?? [], SettingsTypeEnumSchema.Experiment),
            ),
          );
          jobSetupStore.setConditionSetup(
            preserveSingleSelection(
              job.conditionSetupFile,
              jobSetupStore.conditionSetup,
              resolveSingleSetting(job.conditionSetupFile, conditionResponse.list ?? [], SettingsTypeEnumSchema.ConditionSetup),
            ),
          );
          jobSetupStore.setModificationRepository(
            preserveSingleSelection(
              job.modificationRepositoryFile,
              jobSetupStore.modificationRepository,
              resolveSingleSetting(job.modificationRepositoryFile, modificationResponse.list ?? [], SettingsTypeEnumSchema.Modification),
            ),
          );
          jobSetupStore.setReportSettings(
            preserveManySelection(job.reportSettingsFile, jobSetupStore.reportSettings, resolveManySettings(job.reportSettingsFile, reportResponse.list ?? [], SettingsTypeEnumSchema.Report)),
          );
          jobSetupStore.setGOSettings(preserveManySelection(job.goFile, jobSetupStore.goSettings, resolveManySettings(job.goFile, goResponse.list ?? [], SettingsTypeEnumSchema.GO)));
          jobSetupStore.setSpectroMineSearchSettings(
            preserveSingleSelection(
              job.searchSettingsFile,
              jobSetupStore.settingsSpectroMineSearch,
              resolveSingleSetting(job.searchSettingsFile, searchSettingsResponse.list ?? [], SettingsTypeEnumSchema.SpectroMineSearch),
            ),
          );
        }
      } catch (error) {
        console.error("Failed to load job for duplication", error);
      }
    };

    loadJob();

    return () => {
      isCancelled = true;
    };
  }, [activeProjectId, loadExistingJobForRerun, location.search, resetCreateJobState]);

  return (
    <div className="fill-all">
      <Card className="fill-all">
        <CardHeader>
          <CardTitle style={{ fontWeight: "bold" }}>{location.search.includes("rerunJobId=") ? "Duplicate Job" : "New Job"}</CardTitle>
        </CardHeader>
        <JobSetupForm isAdvancedSettingsExpanded={isAdvancedSettingsExpanded} onAdvancedSettingsExpandedChange={onAdvancedSettingsExpandedChange} />
      </Card>
    </div>
  );
};

/**
 * Job setup page where new jobs can be created using a form.
 * @returns Functional component
 */
const JobSetupPage: React.FC = () => {
  const location = useLocation();
  const { projectId: filterProjectId } = useWorkspaceFilters();
  const activeProjectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const [panes, setPanes] = React.useState([
    { size: "30%", min: "15%" }, // Left pane
    { min: "15%" }, // Right pane
  ]);
  const [isAdvancedSettingsExpanded, setIsAdvancedSettingsExpanded] = React.useState(false);
  const libraryRepo = useInjection(LibraryRepository);
  const archiveRepo = useInjection(ArchiveRepository);

  const [availableLibraries, setAvailableLibraries] = React.useState<FileMetadata[]>([]);
  const [isLibrariesLoading, setIsLibrariesLoading] = React.useState<boolean>(false);
  const [availableArchiveJobs, setAvailableArchiveJobs] = React.useState<ArchiveGridItem[] | undefined>(undefined);
  const [isArchivesLoading, setIsArchivesLoading] = React.useState<boolean>(false);

  const { pipelineType, rightPanelTab, setRightPanelTab, resumeFromStepId, software, ...settingsSetters } = useJobSetupStore(
    useShallow((state) => ({
      pipelineType: state.CreateJobState.pipelineType,
      rightPanelTab: state.rightPanelTab,
      setRightPanelTab: state.setRightPanelTab,
      resumeFromStepId: state.CreateJobState.resumeFromStepId,
      software: state.CreateJobState.software,
      setDirectDIASettings: state.setDirectDIASettings,
      setReportSettings: state.setReportSettings,
      setGOSettings: state.setGOSettings,
      setModificationRepository: state.setModificationRepository,
      setConditionSetup: state.setConditionSetup,
      setSpectroMineSearchSettings: state.setSpectroMineSearchSettings,
    })),
  );
  const isRerunMode = React.useMemo(() => new URLSearchParams(location.search).has("rerunJobId"), [location.search]);
  const visibleTabKeys = React.useMemo(
    () => getVisibleRightPanelTabKeys(pipelineType ?? JobPipelineTypeSchema.DirectDIA, resumeFromStepId, isRerunMode, software, isAdvancedSettingsExpanded),
    [pipelineType, resumeFromStepId, isRerunMode, software, isAdvancedSettingsExpanded],
  );
  const isShowRawFilesTab = visibleTabKeys.includes("raw");
  const isShowFastaTab = visibleTabKeys.includes("fasta");
  const standardSettingsSections = React.useMemo(() => getVisibleSettingsSections(software, pipelineType, false), [software, pipelineType]);
  const advancedSettingsSections = React.useMemo(
    () => (isAdvancedSettingsExpanded ? getVisibleSettingsSections(software, pipelineType, true) : []),
    [software, pipelineType, isAdvancedSettingsExpanded],
  );
  const SETTINGS_SETTER_BY_TYPE = {
    Experiment: settingsSetters.setDirectDIASettings,
    Report: settingsSetters.setReportSettings,
    GO: settingsSetters.setGOSettings,
    Modification: settingsSetters.setModificationRepository,
    ConditionSetup: settingsSetters.setConditionSetup,
    SpectroMineSearch: settingsSetters.setSpectroMineSearchSettings,
  } as const;
  const isShowArchiveTab = visibleTabKeys.includes("archives");
  const isShowLibraryTab = visibleTabKeys.includes("libraries");

  const handleTabSelect = (e: TabStripSelectEventArguments) => {
    setRightPanelTab(e.selected);
  };

  const handleAdvancedSettingsExpandedChange = (isExpanded: boolean) => {
    setIsAdvancedSettingsExpanded(isExpanded);
    if (!isExpanded) {
      setRightPanelTab(0);
    }
  };

  // If pipeline changes and hides the current tab, switch to first tab
  React.useEffect(() => {
    if (rightPanelTab >= visibleTabKeys.length) {
      setRightPanelTab(0);
    }
  }, [rightPanelTab, visibleTabKeys]);

  const fetchLibraries = async () => {
    if (!activeProjectId) return;

    setIsLibrariesLoading(true);
    try {
      const response = await libraryRepo.getLibraries(activeProjectId);
      if (Array.isArray(response)) {
        setAvailableLibraries(response);
      } else if ((response as any).data && Array.isArray((response as any).data)) {
        setAvailableLibraries((response as any).data);
      } else {
        setAvailableLibraries([]);
      }
    } catch (error) {
      console.error("Error fetching libraries:", error);
    } finally {
      setIsLibrariesLoading(false);
    }
  };

  const fetchArchiveJobs = async () => {
    setIsArchivesLoading(true);
    try {
      const jobs = await archiveRepo.getArchiveCandidateJobs();

      if (Array.isArray(jobs) && jobs.length > 0) {
        const jobItems: ArchiveGridItem[] = jobs.map((job) => ({
          id: job.id,
          name: job.name || job.id,
          path: job.archivesPath || job.id,
          isFolder: true,
          createdAt: job.createdAt,
          jobCreatedAt: job.createdAt,
          jobAnalysisName: job.analysisName || undefined,
        }));

        setAvailableArchiveJobs(jobItems);
      } else {
        setAvailableArchiveJobs([]);
      }
    } catch (error) {
      console.error("Error fetching archive jobs:", error);
      setAvailableArchiveJobs([]);
    } finally {
      setIsArchivesLoading(false);
    }
  };

  // Preload libraries and archives on mount, but wait for idle time to avoid blocking initial render
  React.useEffect(() => {
    const loadData = () => {
      fetchLibraries();
      // Only fetch archives if pipeline type requires it?
      // The user said "start loading even before clicking on that tab".
      // So we fetch it regardless, or if the pipeline type *could* show it.
      // Since pipelineType defaults to DirectDIA, it is likely shown.
      fetchArchiveJobs();
    };

    // Use requestIdleCallback if available to load data when the main thread is free
    if ("requestIdleCallback" in globalThis) {
      const handle = requestIdleCallback(loadData);
      return () => cancelIdleCallback(handle);
    }

    // Fallback for browsers that don't support requestIdleCallback
    const timeout = setTimeout(loadData, 1000);
    return () => clearTimeout(timeout);
  }, [activeProjectId]);

  const onChange = (event: SplitterOnChangeEvent) => {
    setPanes(event.newState as Array<{ size: string; min: string }>);
  };

  if (!filterProjectId) return <p role="status">Select a project in the header to set up a job.</p>;
  if (activeProjectId !== filterProjectId) return <p role="status">Loading selected project...</p>;

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        <div style={pageHeaderStyle}>
          <h1 style={pageTitleStyle}>Job Setup</h1>
        </div>
      </div>
      <div style={pageFillContentStyle}>
        <Splitter style={{ height: "100%", width: "100%" }} orientation="horizontal" panes={panes} onChange={onChange}>
          <SplitterPane orientation="vertical" overlay={false} containsSplitter={false}>
            <CreateJobFormCard isAdvancedSettingsExpanded={isAdvancedSettingsExpanded} onAdvancedSettingsExpandedChange={handleAdvancedSettingsExpandedChange} />
          </SplitterPane>
          <SplitterPane orientation="vertical" overlay={false} containsSplitter={false}>
            <div className="fill-all">
              <Card className="fill-all" style={{ display: "flex", flexDirection: "column" }}>
                <CardHeader style={{ flex: "0 0 auto" }}>
                  <CardTitle style={{ fontWeight: "bold" }}>Data Explorer</CardTitle>
                </CardHeader>
                <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <TabStrip id="ms-run-selection-tabs" selected={rightPanelTab} onSelect={handleTabSelect} style={{ height: "100%" }}>
                    {isShowRawFilesTab && (
                      <TabStripTab title="Raw Files">
                        <div style={{ height: "100%", padding: "10px 0", boxSizing: "border-box" }}>
                          <FileExplorerGrid select={true} />
                        </div>
                      </TabStripTab>
                    )}
                    {isShowFastaTab && (
                      <TabStripTab title="Fasta">
                        <div style={{ height: "100%", padding: "10px 0", boxSizing: "border-box" }}>
                          <FastaExplorerGrid />
                        </div>
                      </TabStripTab>
                    )}
                    {standardSettingsSections.map((section) => (
                      <TabStripTab key={section.settingsType} title={section.label}>
                        <div style={{ height: "100%", padding: "10px 0", boxSizing: "border-box" }}>
                          <SoftwareSettingsExplorerGrid
                            settingsType={section.settingsType}
                            selection={section.selection}
                            softwareScoped={section.softwareScoped}
                            label={section.label}
                            onSelectionChange={(selected) => SETTINGS_SETTER_BY_TYPE[section.settingsType as keyof typeof SETTINGS_SETTER_BY_TYPE](selected)}
                          />
                        </div>
                      </TabStripTab>
                    ))}
                    {isShowArchiveTab && (
                      <TabStripTab
                        title={
                          isArchivesLoading ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                              Archives <Loader size="small" type="infinite-spinner" />
                            </span>
                          ) : (
                            "Archives"
                          )
                        }
                      >
                        <div style={{ height: "100%", padding: "10px 0", boxSizing: "border-box" }}>
                          <ArchiveExplorerGrid onLoadingChange={setIsArchivesLoading} preloadedItems={availableArchiveJobs} />
                        </div>
                      </TabStripTab>
                    )}
                    {advancedSettingsSections.map((section) => (
                      <TabStripTab key={section.settingsType} title={section.label}>
                        <div style={{ height: "100%", padding: "10px 0", boxSizing: "border-box" }}>
                          <SoftwareSettingsExplorerGrid
                            settingsType={section.settingsType}
                            selection={section.selection}
                            softwareScoped={section.softwareScoped}
                            label={section.label}
                            onSelectionChange={(selected) => SETTINGS_SETTER_BY_TYPE[section.settingsType as keyof typeof SETTINGS_SETTER_BY_TYPE](selected)}
                          />
                        </div>
                      </TabStripTab>
                    ))}
                    {isShowLibraryTab && (
                      <TabStripTab
                        title={
                          isLibrariesLoading ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                              Libraries <Loader size="small" type="infinite-spinner" />
                            </span>
                          ) : (
                            "Libraries"
                          )
                        }
                      >
                        <div style={{ height: "100%", padding: "10px 0", boxSizing: "border-box" }}>
                          <LibraryExplorerGrid libraries={availableLibraries} loading={isLibrariesLoading} onReload={fetchLibraries} />
                        </div>
                      </TabStripTab>
                    )}
                  </TabStrip>
                </div>
              </Card>
            </div>
          </SplitterPane>
        </Splitter>
      </div>
    </div>
  );
};

export default JobSetupPage;
